import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem as SelectMenuItem,
  Tabs,
  Tab,
  SelectChangeEvent,
  Avatar,
  Backdrop,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  Search,
  UnfoldMore,
  Public as PublicIcon,
  
} from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { offerStandaloneService, type OfferStandalone, type CreateOfferStandaloneRequest } from '../../services/offerStandaloneService';
import userService, { AppUser } from '../../services/userService';
import { useNotification } from '../layouts/Layout';
import AddOfferStandaloneForm from './AddOfferStandaloneForm';
import AddOfferAIForm from './AddOfferAIForm';
import OfferDetails from './OfferDetails';
import { useAuth } from '../../contexts/AuthContext';
import { getBoardUrlForOffer } from '../../services/collectifileService';
import RulerIcon from '../icons/RulerIcon';
import { getSocket } from '../../services/realtimeService';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`offer-tabpanel-${index}`}
      aria-labelledby={`offer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
}

interface OffersStandaloneTabProps {
  onRefreshNeeded?: () => void;
}

const OffersStandaloneTab: React.FC<OffersStandaloneTabProps> = ({ onRefreshNeeded }) => {
  // Stan dla ofert
  const [offers, setOffers] = useState<OfferStandalone[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<OfferStandalone | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const theme = useTheme();
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [tableHeight, setTableHeight] = React.useState<number | null>(null);
  // Tryb zmiany szerokości kolumn – jak w ProjectsPage
  const [colResizeMode, setColResizeMode] = useState<boolean>(false);
  const [dragCol, setDragCol] = useState<{ key: OfferColumnKey; startX: number; startW: number } | null>(null);
  const OFFERS_WIDTHS_KEY = React.useMemo(() => `offersTableColWidths:${user?.user_id || 'anon'}:widths`, [user?.user_id]);
  const DEFAULT_COL_WIDTHS: Record<OfferColumnKey, number> = {
    oferta: 520,
    preparer: 46,
    client: 260,
    location: 110,
    twl: 60,
    tso: 80,
    swz: 90,
    wadium: 70,
    status: 80,
    icons: 170
  };
  const [columnWidths, setColumnWidths] = useState<Record<OfferColumnKey, number>>({ ...DEFAULT_COL_WIDTHS });
  const getColWidthPx = (key: OfferColumnKey): string => `${Math.max(30, columnWidths[key] ?? DEFAULT_COL_WIDTHS[key] ?? 40)}px`;
  type OfferColumnKey = 'oferta' | 'preparer' | 'client' | 'location' | 'twl' | 'tso' | 'swz' | 'wadium' | 'status' | 'icons';
  const DEFAULT_VIS: Record<OfferColumnKey, boolean> = { oferta: true, preparer: true, client: true, location: true, twl: true, tso: true, swz: true, wadium: true, status: true, icons: true } as const;
  const [columnsVisible, setColumnsVisible] = useState<Record<OfferColumnKey, boolean>>(() => {
    try {
      const saved = localStorage.getItem('offers.columns.visible');
      if (saved) return JSON.parse(saved);
    } catch {}
    return { ...DEFAULT_VIS } as Record<OfferColumnKey, boolean>;
  });
  const [colsAnchorEl, setColsAnchorEl] = useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [draftColumnsVisible, setDraftColumnsVisible] = useState<Record<OfferColumnKey, boolean> | null>(null);
  // Styl nagłówków kolumn: zgodny z ProjectsPage
  const sortLabelBaseSx = {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    color: '#2D2D32',
    display: 'inline-flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: '4px',
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      width: 12,
      height: 12,
      ml: '4px'
    }
  } as const;
  const StrongSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#2D2D32' }} />);
  const MutedSortIcon = () => (<UnfoldMore sx={{ width: 12, height: 12, color: '#d4d4d9' }} />);
  const getSortLabelProps = (isActive: boolean) => ({
    IconComponent: (isActive ? StrongSortIcon : MutedSortIcon) as any,
    sx: { 
      ...sortLabelBaseSx, 
      '& .MuiTableSortLabel-icon': { 
        ...(sortLabelBaseSx as any)['& .MuiTableSortLabel-icon'], 
        color: isActive ? '#2D2D32' : '#d4d4d9' 
      } 
    }
  }) as const;
  
  // Stan dla filtrów
  const [searchText, setSearchText] = useState('');
  const [preparer, setPreparer] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const startDateRef = React.useRef<HTMLInputElement | null>(null);
  const endDateRef = React.useRef<HTMLInputElement | null>(null);
  // Usuwamy zaznaczanie wierszy; na klik nawigujemy do szczegółów
  const [users, setUsers] = useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);

  // Sortowanie
  type SortField = 'id' | 'offer_preparer' | 'client_name' | 'order_name' | 'location' | 'site_inspection_date' | 'submission_deadline' | 'estimated_order_value' | 'bid_bond' | 'status';
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Stan dla formularzy
  const [openAddForm, setOpenAddForm] = useState(false);
  
  // Eksportuj funkcję otwierania formularza przez window, aby OffersPage mógł ją wywołać
  useEffect(() => {
    (window as any).__openOffersAddForm = () => {
      setOpenAddForm(true);
    };
    return () => {
      delete (window as any).__openOffersAddForm;
    };
  }, []);
  const [openAddFormFromWand, setOpenAddFormFromWand] = useState(false);
  const [aiGeneratedData, setAiGeneratedData] = useState<CreateOfferStandaloneRequest | null>(null);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState<OfferStandalone | null>(null);
  
  // Stan dla menu akcji
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOfferId, setMenuOfferId] = useState<string | null>(null);
  
  // Stan dla ładowania
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();
  
  // Sync zakładek między użytkownikami (emit i nasłuch)
  const handleTabChange = (_: any, next: number) => { setTabValue(next); try { getSocket().emit('ui:tab:set', { page: 'offers', tab: next }); } catch {} };
  useEffect(() => {
    const onExternal = (e: any) => { if (e?.detail?.page === 'offers' && typeof e.detail.tab === 'number') setTabValue(e.detail.tab); };
    window.addEventListener('ui:tab:set:external', onExternal as any);
    return () => window.removeEventListener('ui:tab:set:external', onExternal as any);
  }, []);

  // Obsługa parametru tab w URL: /offers?tab=0/1/2/3
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get('tab');
      if (tabParam !== null) {
        const tabNum = parseInt(tabParam, 10);
        if (!isNaN(tabNum) && tabNum >= 0 && tabNum <= 3) {
          setTabValue(tabNum);
        }
      }
    } catch {}
  }, []);

  // Pobieranie ofert
  useEffect(() => {
    fetchOffers();
  }, []);

  // Dopasowanie wysokości tabeli jak w projektach (ok. 9 wierszy + header)
  useEffect(() => {
    const measureHeights = () => {
      try {
        const root = tableContainerRef.current as HTMLElement | null;
        if (!root) return;
        const headerRow = root.querySelector('thead tr') as HTMLElement | null;
        const bodyRows = root.querySelectorAll('tbody tr');
        const headerH = Math.round(headerRow?.getBoundingClientRect().height || 32);
        let rowH = 36;
        const sampleCount = Math.min(12, bodyRows.length);
        if (sampleCount > 0) {
          let maxH = 0;
          for (let i = 0; i < sampleCount; i++) {
            const h = (bodyRows[i] as HTMLElement).getBoundingClientRect().height;
            if (h && h > maxH) maxH = h;
          }
          if (maxH > 0) rowH = Math.round(maxH);
        }
        const borders = 2;
        const fudgePx = 20;
        const heightForNineRows = headerH + (rowH * 9) + borders + fudgePx;
        const top = root.getBoundingClientRect().top;
        const viewportAvail = Math.max(300, Math.floor((window.innerHeight || 0) - top - 16));
        const finalHeight = Math.max(heightForNineRows, viewportAvail);
        setTableHeight(finalHeight);
      } catch {}
    };
    const t = setTimeout(measureHeights, 0);
    const t2 = setTimeout(measureHeights, 100);
    window.addEventListener('resize', measureHeights);
    return () => { clearTimeout(t); clearTimeout(t2); window.removeEventListener('resize', measureHeights); };
  }, [offers.length, tabValue]);

  // Wczytaj/zapisz szerokości kolumn
  useEffect(() => {
    try {
      const raw = localStorage.getItem(OFFERS_WIDTHS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [OFFERS_WIDTHS_KEY]);
  useEffect(() => {
    try { localStorage.setItem(OFFERS_WIDTHS_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [OFFERS_WIDTHS_KEY, columnWidths]);

  // Obsługa przeciągania separatorów kolumn (jak w projektach)
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      e.preventDefault();
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(24, dragCol.startW + dx);
      setColumnWidths(prev => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = (e: MouseEvent) => { e.preventDefault(); setDragCol(null); };
    if (dragCol) {
      try { document.body.style.cursor = 'col-resize'; } catch {}
      window.addEventListener('mousemove', onMove, { passive: false } as any);
      window.addEventListener('mouseup', onUp as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('mouseup', onUp as any);
      try { document.body.style.cursor = ''; } catch {}
    };
  }, [dragCol]);

  // ESC wychodzi z trybu zmiany szerokości, gdy panel nie jest otwarty
  useEffect(() => {
    if (!(colResizeMode || colsOpen)) return;
    const onKey = (e: KeyboardEvent) => { 
      if (e.key === 'Escape') { 
        e.preventDefault(); 
        setDragCol(null); 
        setColResizeMode(false); 
        setColsAnchorEl(null);
        setDraftColumnsVisible(null);
      } 
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [colResizeMode, colsOpen]);

  useEffect(() => {
    userService.listUsersPublicForBusinessForms().then(setUsers).catch(() => {});
  }, []);

  // Domyślne sortowanie po TSO rosnąco; brak zapamiętywania preferencji między odświeżeniami
  
  const handlePreparerChange = (event: SelectChangeEvent) => {
    setPreparer(event.target.value as string);
  };

  const navigate = useNavigate();

  const handleRequestSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  
  // Obsługa menu akcji
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, offerId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuOfferId(offerId);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOfferId(null);
  };
  
  const fetchOffers = async () => {
    try {
      setLoading(true);
      const data = await offerStandaloneService.getAllOffers();
      setOffers(data);
    } catch (error) {
      console.error('Błąd podczas pobierania ofert:', error);
      showNotification('Nie udało się pobrać ofert', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Obsługa dodawania oferty
  const handleAddOffer = (newOffer: OfferStandalone) => {
    // Odśwież listę ofert z serwera, aby mieć aktualne dane
    fetchOffers();
    setOpenAddForm(false);
    showNotification('Oferta została dodana pomyślnie', 'success');
    // Wywołaj callback jeśli został przekazany
    if (onRefreshNeeded) {
      onRefreshNeeded();
    }
  };
  
  // Obsługa edycji oferty
  const handleEditOffer = async (updatedOffer: OfferStandalone) => {
    setOffers(offers.map(offer => 
      offer.id === updatedOffer.id ? updatedOffer : offer
    ));
    setOpenEditForm(false);
    setEditingOffer(null);
    showNotification('Oferta została zaktualizowana pomyślnie', 'success');
  };
  
  // Obsługa usuwania oferty
  const handleDeleteOffer = async () => {
    if (!selectedOffer) return;
    
    try {
      setLoading(true);
      await offerStandaloneService.deleteOffer(selectedOffer.id);
      
      setOffers(offers.filter(offer => offer.id !== selectedOffer.id));
      setOpenDeleteDialog(false);
      setSelectedOffer(null);
      showNotification('Oferta została usunięta pomyślnie', 'success');
    } catch (error: any) {
      console.error('Błąd podczas usuwania oferty:', error);
      showNotification(error.message || 'Wystąpił błąd podczas usuwania oferty', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filtrowanie ofert według statusu
  const todayStart = React.useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const offersByStatus = {
    'offered': offers.filter(offer => 
      ['w trakcie', 'najlepsza oferta', 'druga oferta', 'trzecia i dalej oferta', 'Uzupełnianie dokumentów'].includes(offer.status)
    ),
    'finished': offers.filter(offer => 
      ['Wygrana', 'odrzucona', 'przegrana'].includes(offer.status)
    ),
    // Bieżące prekwalifikacje: bez TSO lub TSO dziś/później
    'prequalification': offers.filter(offer => {
      if (offer.status !== 'Prekwalifikacja') return false;
      if (!offer.submission_deadline) return true;
      try {
        const tso = new Date(offer.submission_deadline);
        return tso.getTime() >= todayStart.getTime();
      } catch {
        return true;
      }
    }),
    // Archiwum prekwalifikowanych: TSO starsze niż dzisiejsza data
    'prequalificationArchive': offers.filter(offer => {
      if (offer.status !== 'Prekwalifikacja') return false;
      if (!offer.submission_deadline) return false;
      try {
        const tso = new Date(offer.submission_deadline);
        return tso.getTime() < todayStart.getTime();
      } catch {
        return false;
      }
    })
  };

  // Wybór ofert dla aktualnej zakładki (Aktualnie ofertowane, Prekwalifikacja, Zakończone, Archiwum prekwalifikowanych)
  let currentStatusOffers = 
    tabValue === 0 ? offersByStatus['offered'] :
    tabValue === 1 ? offersByStatus['prequalification'] :
    tabValue === 2 ? offersByStatus['finished'] :
    offersByStatus['prequalificationArchive'];

  // Filtrowanie ofert
  const filteredOffers = currentStatusOffers.filter(offer => {
    // Filtrowanie po tekście wyszukiwania
    const matchesSearch = searchText === '' || 
      offer.id.toLowerCase().includes(searchText.toLowerCase()) ||
      offer.client_name.toLowerCase().includes(searchText.toLowerCase()) ||
      offer.order_name.toLowerCase().includes(searchText.toLowerCase());
    
    // Filtrowanie po osobie przygotowującej
    const matchesPreparer = preparer === '' || offer.offer_preparer === preparer;
    
    // Filtrowanie po dacie początkowej
    const matchesStartDate = startDate === '' || 
      new Date(offer.submission_deadline) >= new Date(startDate);
    
    // Filtrowanie po dacie końcowej
    const matchesEndDate = endDate === '' || 
      new Date(offer.submission_deadline) <= new Date(endDate);
    
    return matchesSearch && matchesPreparer && matchesStartDate && matchesEndDate;
  });

  // Funkcja do sprawdzania filtrów dla liczników zakładek
  const matchesOfferFilters = React.useCallback((offer: OfferStandalone) => {
    const matchesSearch =
      searchText === '' ||
      offer.id.toLowerCase().includes(searchText.toLowerCase()) ||
      (offer.client_name || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (offer.order_name || '').toLowerCase().includes(searchText.toLowerCase());
    const matchesPreparer = preparer === '' || offer.offer_preparer === preparer;
    const matchesStartDate = startDate === '' || new Date(offer.submission_deadline) >= new Date(startDate);
    const matchesEndDate = endDate === '' || new Date(offer.submission_deadline) <= new Date(endDate);
    return matchesSearch && matchesPreparer && matchesStartDate && matchesEndDate;
  }, [searchText, preparer, startDate, endDate]);

  // Liczniki zakładek po filtrach
  const tabCounts = React.useMemo(() => ({
    'offered': offersByStatus['offered'].filter(matchesOfferFilters).length,
    'prequalification': offersByStatus['prequalification'].filter(matchesOfferFilters).length,
    'finished': offersByStatus['finished'].filter(matchesOfferFilters).length,
    'prequalificationArchive': offersByStatus['prequalificationArchive'].filter(matchesOfferFilters).length,
  }), [offersByStatus['offered'], offersByStatus['prequalification'], offersByStatus['finished'], offersByStatus['prequalificationArchive'], matchesOfferFilters]);

  // Porównywarka dla sortowania
  const getComparable = (offer: OfferStandalone, field: SortField) => {
    switch (field) {
      case 'id': {
        if (typeof offer.offer_sequence_number === 'number') return offer.offer_sequence_number;
        if (offer.offer_id) return offer.offer_id;
        return offer.id;
      }
      case 'offer_preparer':
        return offer.offer_preparer || '';
      case 'client_name':
        return offer.client_name || '';
      case 'order_name':
        return offer.order_name || '';
      case 'location':
        return offer.location || '';
      case 'site_inspection_date':
        return offer.site_inspection_date ? new Date(offer.site_inspection_date).getTime() : 0;
      case 'submission_deadline':
        return offer.submission_deadline ? new Date(offer.submission_deadline).getTime() : 0;
      case 'estimated_order_value':
        return typeof offer.estimated_order_value === 'number' ? offer.estimated_order_value : -1;
      case 'bid_bond':
        return typeof offer.bid_bond === 'number' ? offer.bid_bond : -1;
      case 'status':
        return offer.status || '';
      default:
        return '';
    }
  };

  const visibleOffers = [...filteredOffers].sort((a, b) => {
    const av = getComparable(a, sortField);
    const bv = getComparable(b, sortField);
    let res = 0;
    if (typeof av === 'number' && typeof bv === 'number') {
      res = av - bv;
    } else {
      res = String(av).localeCompare(String(bv), 'pl', { sensitivity: 'base', numeric: true });
    }
    return sortOrder === 'asc' ? res : -res;
  });
  
  // Renderowanie statusu oferty
  const renderStatus = (status: string) => {
    // Pastelowe tła + ciemny tekst (#020242) inspirowane stylem z figmy
    const text = '#020242';
    const map: Record<string, { bg: string; fg: string }> = {
      'w trakcie': { bg: '#E6F0FF', fg: text },
      'najlepsza oferta': { bg: '#EAF7EA', fg: text },
      'druga oferta': { bg: '#FEEED6', fg: text }, // z figmy (pastelowy pomarańcz)
      'trzecia i dalej oferta': { bg: '#EFE6FF', fg: text },
      'Uzupełnianie dokumentów': { bg: '#E6F7FF', fg: text },
      'Wygrana': { bg: '#E6F5E6', fg: text },
      'odrzucona': { bg: '#FFE6E6', fg: text },
      'przegrana': { bg: '#FFE6E6', fg: text },
      'Prekwalifikacja': { bg: '#FFF5CC', fg: text },
    };
    const palette = map[status] || { bg: '#F1F1F6', fg: text };
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: palette.bg,
          color: palette.fg,
          borderRadius: '4px',
          minHeight: 21,
          height: 21,
          '& .MuiChip-label': {
            px: 1,
            py: 0,
            fontFamily: 'Roboto, sans-serif',
            fontSize: '11px',
            fontWeight: 400,
            lineHeight: '13px',
            color: palette.fg
          }
        }}
      />
    );
  };
  
  // Formatowanie daty
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd');
    } catch (error) {
      return dateString;
    }
  };
  
  // Formatowanie ceny
  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return '-';
    return new Intl.NumberFormat('pl-PL', { 
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Style dla komórek tabeli
  const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '140px',
    padding: '2px 6px',
    fontSize: '0.72rem'
  };
  const clientCellStyle = { ...cellStyle, maxWidth: '260px' } as const;
  const ofertaCellStyle = { ...cellStyle, maxWidth: '520px', paddingLeft: '4px' } as const;

  // Renderowanie tabeli ofert
  const renderOffersTable = () => (
    <TableContainer ref={tableContainerRef} component={Paper} sx={{ height: tableHeight ?? 432, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', overflowY: 'auto', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
      <Table size="small" stickyHeader sx={{
        tableLayout: 'fixed',
        width: '100%',
        '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
        '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
        '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
        '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 },
        '& .MuiTableCell-head, & .MuiTableCell-head .MuiTableSortLabel-root': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', color: '#2D2D32' }
      }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('oferta'), pl: 0, position: 'relative', display: columnsVisible['oferta'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'id')} active={sortField === 'id'} direction={sortOrder}
                onClick={() => handleRequestSort('id')}>
                Oferta
              </TableSortLabel>
              {(colResizeMode) && (
                <Box
                  role="separator"
                  aria-orientation="vertical"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'oferta', startX: e.clientX, startW: parseInt(getColWidthPx('oferta')) || (columnWidths['oferta'] ?? DEFAULT_COL_WIDTHS['oferta']) }); }}
                  sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }}
                />
              )}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('preparer'), position: 'relative', display: columnsVisible['preparer'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'offer_preparer')} active={sortField === 'offer_preparer'} direction={sortOrder}
                onClick={() => handleRequestSort('offer_preparer')}>
                KP
              </TableSortLabel>
              {(colResizeMode) && (
                <Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'preparer', startX: e.clientX, startW: parseInt(getColWidthPx('preparer')) || (columnWidths['preparer'] ?? DEFAULT_COL_WIDTHS['preparer']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />
              )}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('client'), position: 'relative', display: columnsVisible['client'] ? 'table-cell' : 'none', userSelect: 'none' }}>Zamawiający{(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'client', startX: e.clientX, startW: parseInt(getColWidthPx('client')) || (columnWidths['client'] ?? DEFAULT_COL_WIDTHS['client']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}</TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('location'), position: 'relative', display: columnsVisible['location'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'location')} active={sortField === 'location'} direction={sortOrder} onClick={() => handleRequestSort('location')}>
                Lokalizacja
              </TableSortLabel>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'location', startX: e.clientX, startW: parseInt(getColWidthPx('location')) || (columnWidths['location'] ?? DEFAULT_COL_WIDTHS['location']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('twl'), position: 'relative', display: columnsVisible['twl'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'site_inspection_date')} active={sortField === 'site_inspection_date'} direction={sortOrder}
                onClick={() => handleRequestSort('site_inspection_date')}>
                TWL
              </TableSortLabel>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'twl', startX: e.clientX, startW: parseInt(getColWidthPx('twl')) || (columnWidths['twl'] ?? DEFAULT_COL_WIDTHS['twl']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('tso'), position: 'relative', display: columnsVisible['tso'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'submission_deadline')} active={sortField === 'submission_deadline'} direction={sortOrder}
                onClick={() => handleRequestSort('submission_deadline')}>
                TSO
              </TableSortLabel>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'tso', startX: e.clientX, startW: parseInt(getColWidthPx('tso')) || (columnWidths['tso'] ?? DEFAULT_COL_WIDTHS['tso']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('swz'), position: 'relative', display: columnsVisible['swz'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'estimated_order_value')} active={sortField === 'estimated_order_value'} direction={sortOrder}
                onClick={() => handleRequestSort('estimated_order_value')}>
                SWZ
              </TableSortLabel>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'swz', startX: e.clientX, startW: parseInt(getColWidthPx('swz')) || (columnWidths['swz'] ?? DEFAULT_COL_WIDTHS['swz']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('wadium'), position: 'relative', display: columnsVisible['wadium'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'bid_bond')} active={sortField === 'bid_bond'} direction={sortOrder}
                onClick={() => handleRequestSort('bid_bond')}>
                Wadium
              </TableSortLabel>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'wadium', startX: e.clientX, startW: parseInt(getColWidthPx('wadium')) || (columnWidths['wadium'] ?? DEFAULT_COL_WIDTHS['wadium']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('status'), position: 'relative', display: columnsVisible['status'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              <TableSortLabel {...getSortLabelProps(sortField === 'status')} active={sortField === 'status'} direction={sortOrder}
                onClick={() => handleRequestSort('status')}>
                Status
              </TableSortLabel>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'status', startX: e.clientX, startW: parseInt(getColWidthPx('status')) || (columnWidths['status'] ?? DEFAULT_COL_WIDTHS['status']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
            {/* Ikony: Drive, Strona zam., Board, Więcej */}
            <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('icons'), position: 'relative', display: columnsVisible['icons'] ? 'table-cell' : 'none', userSelect: 'none' }}>
              {(colResizeMode) && (<Box role="separator" aria-orientation="vertical" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: 'icons', startX: e.clientX, startW: parseInt(getColWidthPx('icons')) || (columnWidths['icons'] ?? DEFAULT_COL_WIDTHS['icons']) }); }} sx={{ position: 'absolute', right: 0, top: 0, width: '12px', height: '100%', cursor: 'col-resize', zIndex: 6, touchAction: 'none', backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' } }} />)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleOffers.map((offer, index) => {
            const displayId = (offer as any).offer_id || ((offer as any).offer_sequence_number && (offer as any).created_at
              ? `OF.${(offer as any).offer_sequence_number}.${new Date((offer as any).created_at).getFullYear()}`
              : offer.id.substring(0,8));
            return (
              <TableRow
                hover
                key={offer.id + index}
                sx={{
                  '&:hover': { backgroundColor: '#F5F5FF !important' },
                  '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' },
                  '&.Mui-selected': { backgroundColor: '#F5F5FF !important' },
                  '&.Mui-selected:hover': { backgroundColor: '#F5F5FF !important' }
                }}
              >
                <TableCell 
                  sx={{ 
                    ...ofertaCellStyle, 
                    maxWidth: 'none', 
                    width: getColWidthPx('oferta'), 
                    display: columnsVisible['oferta'] ? 'table-cell' : 'none'
                  }}
                >
                  <span
                    style={{
                      fontSize: '0.86rem',
                      color: '#2D2D32',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.25,
                      whiteSpace: 'normal'
                    }}
                  >
                    <RouterLink
                      to={`/offers/${encodeURIComponent(displayId)}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        textDecoration: 'underline',
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#3F3FDC'
                      }}
                    >
                      {displayId}
                    </RouterLink>
                    <span>{` — ${offer.order_name || ''}`}</span>
                  </span>
                </TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('preparer'), px: '2px', display: columnsVisible['preparer'] ? 'table-cell' : 'none' }}>
                  {offer.offer_preparer === '__AI__' ? (
                    <Avatar sx={{ width: 22, height: 22, bgcolor: 'transparent', p: 0 }} title="Wprowadzone przez AI">
                      <img src="/assets/ai_badge_unitresco.svg" alt="AI" width={22} height={22} style={{ display: 'block' }} />
                    </Avatar>
                  ) : offer.offer_preparer ? (
                    <Avatar src={offer.offer_preparer_avatar_url || undefined} sx={{ width: 22, height: 22, fontSize: '0.75rem' }}>
                      {(offer.offer_preparer_name || '?').slice(0,1)}
                    </Avatar>
                  ) : '-'}
                </TableCell>
                <TableCell sx={{ ...clientCellStyle, maxWidth: 'none', width: getColWidthPx('client'), display: columnsVisible['client'] ? 'table-cell' : 'none' }}>
                  <Box
                    sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.25,
                      whiteSpace: 'normal'
                    }}
                  >
                    {offer.client_name}
                  </Box>
                </TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('location'), display: columnsVisible['location'] ? 'table-cell' : 'none' }}>
                  {offer.location || '-'}
                </TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('twl'), display: columnsVisible['twl'] ? 'table-cell' : 'none' }}>
                  {offer.site_inspection === 'Tak' && offer.site_inspection_date 
                    ? formatDate(offer.site_inspection_date) 
                    : '-'}
                </TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('tso'), display: columnsVisible['tso'] ? 'table-cell' : 'none' }}>{formatDate(offer.submission_deadline)}</TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('swz'), display: columnsVisible['swz'] ? 'table-cell' : 'none' }}>{formatPrice(offer.estimated_order_value)}</TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('wadium'), display: columnsVisible['wadium'] ? 'table-cell' : 'none' }}>{formatPrice(offer.bid_bond)}</TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('status'), display: columnsVisible['status'] ? 'table-cell' : 'none' }}>{renderStatus(offer.status)}</TableCell>
                <TableCell sx={{ ...cellStyle, maxWidth: 'none', width: getColWidthPx('icons'), px: 0, display: columnsVisible['icons'] ? 'table-cell' : 'none' }} onClick={(e)=> e.stopPropagation()} onMouseDown={(e)=> e.stopPropagation()}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, justifyContent: 'flex-start', px: 0.5 }}>
                    <IconButton size="small" sx={{ p: 0.25 }} disabled={!offer.google_drive_link} onClick={() => offer.google_drive_link && window.open(offer.google_drive_link, '_blank')}>
                      <Box component="img" src="/assets/icons/drive.svg" alt="Google Drive" />
                    </IconButton>
                    <IconButton size="small" sx={{ p: 0.25 }} disabled={!offer.client_website} onClick={() => offer.client_website && window.open(offer.client_website, '_blank')}>
                      <Box
                        component="img"
                        src="/assets/icons/planet.svg"
                        alt="Strona"
                        onError={(e:any)=>{ try { e.currentTarget.src = '/assets/icons/frame-344.svg'; } catch {} }}
                      />
                    </IconButton>
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => window.open(getBoardUrlForOffer(displayId, (offer as any).collectifile_board_id, offer.miro_link), '_blank')}>
                      <RulerIcon filled={!!(offer as any).collectifile_board_id || !!offer.miro_link} sx={{ width: 24, height: 24 }} />
                    </IconButton>
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => window.open(`/cad/${encodeURIComponent(displayId)}`, '_blank')} aria-label="CAD (DWG/DXF)">
                      <Box component="img" src="/assets/icons/architecture.svg" alt="CAD" />
                    </IconButton>
                    <IconButton size="small" sx={{ p: 0.25 }} onClick={(event) => handleOpenMenu(event, offer.id)} aria-label="akcje">
                      <Box component="img" src="/assets/icons/more.svg" alt="Więcej" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      
    </TableContainer>
  );
  
  return (
    <Box>
      {/* Pola wyszukiwania i filtrów */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: '300px 300px 200px 200px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Search 300x36, white bg, #d4d4d9 border */}
        <TextField
          size="small"
          placeholder="Szukaj w ofertach"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{ 
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {}
              }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Przygotowuje ofertę 300x36 */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Przygotowuje ofertę</Typography>
          <Select
            size="small"
            value={preparer}
            onChange={handlePreparerChange}
            displayEmpty
            renderValue={preparer !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{ 
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: preparer ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <SelectMenuItem value="">Wszyscy</SelectMenuItem>
            {users.map(u => (
              <SelectMenuItem key={u.user_id} value={u.user_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22 }} />
                  <Box component="span">{u.display_name || u.email}</Box>
                </Box>
              </SelectMenuItem>
            ))}
            <SelectMenuItem value="AI">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src="/assets/ai_badge_unitresco.svg" sx={{ width: 22, height: 22 }} />
                <Box component="span">AI</Box>
              </Box>
            </SelectMenuItem>
          </Select>
        </Box>

        {/* Data od 200x36 */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data od</Typography>
          <TextField
            size="small"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            variant="outlined"
            inputRef={startDateRef}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => { try { (startDateRef.current as any)?.showPicker?.(); } catch { startDateRef.current?.focus?.(); } }}
                >
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: startDate ? '#2D2D32' : '#B9B9B9' },
              '& input[type="date"]::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>

        {/* Data do 200x36 */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Data do</Typography>
          <TextField
            size="small"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            variant="outlined"
            inputRef={endDateRef}
            InputProps={{
              startAdornment: (
                <InputAdornment 
                  position="start" 
                  sx={{ cursor: 'pointer' }}
                  onClick={() => { try { (endDateRef.current as any)?.showPicker?.(); } catch { endDateRef.current?.focus?.(); } }}
                >
                  <Box component="img" src="/assets/filters/calendar_today.svg" alt="calendar" />
                </InputAdornment>
              )
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { height: 36, backgroundColor: '#fff', borderRadius: '4px' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
              '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: endDate ? '#2D2D32' : '#B9B9B9' },
              '& input[type="date"]::-webkit-calendar-picker-indicator': { opacity: 0, display: 'none' },
              '& input::-ms-clear': { display: 'none' }
            }}
          />
        </Box>
      </Box>

      {/* Tabs + akcje po prawej – pionowo jak w ProjectDetails (bez dodatkowego marginesu) */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '2px', height: 40, mt: 0, mb: 0 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="offer tabs"
          sx={{ 
            minHeight: 40,
            height: 40,
            flexGrow: 1,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTabs-flexContainer': { gap: '2px' }
          }}
        >
          <Tab 
            disableRipple
            label={`Aktualnie ofertowane (${tabCounts['offered']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab 
            disableRipple
            label={`Prekwalifikacja (${tabCounts['prequalification']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab 
            disableRipple
            label={`Zakończone (${tabCounts['finished']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
          <Tab 
            disableRipple
            label={`Archiwum prekwalifikowanych (${tabCounts['prequalificationArchive']})`} 
            sx={{
              textTransform: 'none',
              color: '#020242',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              px: 3,
              minHeight: 32,
              height: 32,
              mt: '8px',
              bgcolor: '#F1F1F6',
              borderRadius: '4px 4px 0 0',
              '&.Mui-selected': { mt: 0, height: 40, bgcolor: '#E6E6EC', color: '#020242', fontWeight: 500, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
              '&.Mui-selected .MuiTab-wrapper': { fontWeight: 500, color: '#020242' }
            }}
          />
        </Tabs>
        {/* Ikona z magiczną różdżką */}
        <IconButton
          size="small"
          sx={{ width: 32, height: 32, borderRadius: '4px', mt: '4px', p: 0, alignSelf: 'flex-end', bgcolor: 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setOpenAddFormFromWand(true)}
        >
          <Box component="img" alt="magiczna różdżka" src={'/assets/icons/wand_shine_24dp_5F6368_FILL0_wght300_GRAD0_opsz24.svg'} sx={{ width: 24, height: 24 }} />
        </IconButton>
        {/* Ikona "Dostosuj kolumny" w tym samym miejscu co w projektach */}
        <IconButton
          size="small"
          sx={{ width: 32, height: 32, borderRadius: '4px', mt: '4px', p: 0, alignSelf: 'flex-end', bgcolor: colsOpen ? '#E0E0F5' : 'transparent', transition: 'background-color 0.15s ease', '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e)=>{ setColResizeMode(true); setColsAnchorEl(e.currentTarget); }}
        >
          <Box component="img" alt="kolumny" src={'/assets/icons/frame-344.svg'} sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleOffers.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Brak ofert w tej kategorii
            </Typography>
          </Paper>
        ) : (
          renderOffersTable()
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleOffers.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Brak ofert w tej kategorii
            </Typography>
          </Paper>
        ) : (
          renderOffersTable()
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleOffers.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Brak ofert w tej kategorii
            </Typography>
          </Paper>
        ) : (
          renderOffersTable()
        )}
      </TabPanel>
      <TabPanel value={tabValue} index={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleOffers.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Brak ofert w tej kategorii
            </Typography>
          </Paper>
        ) : (
          renderOffersTable()
        )}
      </TabPanel>

      {/* Usunięto podgląd wiersza — klik przenosi do strony oferty */}
      
      {/* Formularz dodawania oferty */}
      <AddOfferStandaloneForm
        open={openAddForm && !aiGeneratedData}
        onClose={() => setOpenAddForm(false)}
        onAdd={handleAddOffer}
      />
      
      {/* Formularz dodawania oferty przez AI z ikony magicznej różdżki */}
      <AddOfferAIForm
        open={openAddFormFromWand}
        onClose={() => {
          setOpenAddFormFromWand(false);
          setAiGeneratedData(null);
        }}
        onGenerated={(formData) => {
          setOpenAddFormFromWand(false);
          setAiGeneratedData(formData);
          setOpenAddForm(true);
        }}
      />
      
      {/* Formularz dodawania oferty z danymi wygenerowanymi przez AI */}
      {aiGeneratedData && (
        <AddOfferStandaloneForm
          open={openAddForm && !!aiGeneratedData}
          onClose={() => {
            setOpenAddForm(false);
            setAiGeneratedData(null);
          }}
          onAdd={(offer) => {
            handleAddOffer(offer);
            setOpenAddForm(false);
            setAiGeneratedData(null);
          }}
          initialData={aiGeneratedData as any}
        />
      )}
      
      {/* Formularz edycji oferty */}
      {editingOffer && (
        <AddOfferStandaloneForm
          open={openEditForm}
          onClose={() => {
            setOpenEditForm(false);
            setEditingOffer(null);
          }}
          onAdd={handleEditOffer}
          isEditMode={true}
          initialData={editingOffer}
        />
      )}
      
      {/* Dialog usuwania oferty */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Potwierdź usunięcie</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć ofertę "{selectedOffer?.order_name}" od {selectedOffer?.client_name}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Tej operacji nie można cofnąć.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            color="inherit"
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleDeleteOffer}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Usuwanie...' : 'Usuń'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Menu akcji */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => {
          const offer = offers.find(o => o.id === menuOfferId);
          if (offer) {
            setSelectedOffer(offer);
            setOpenDetailsDialog(true);
          }
          handleCloseMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            Szczegóły
          </Box>
        </MenuItem>
        {offers.find(o => o.id === menuOfferId)?.google_drive_link && (
          <MenuItem onClick={() => {
            const offer = offers.find(o => o.id === menuOfferId);
            if (offer?.google_drive_link) {
              window.open(offer.google_drive_link, '_blank');
            }
            handleCloseMenu();
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LinkIcon fontSize="small" sx={{ mr: 1 }} />
              Google Drive
            </Box>
          </MenuItem>
        )}
        {/* DWG/DXF – dostępne pod ikoną CAD (architecture) w wierszu */}
        <MenuItem onClick={() => {
          const offer = offers.find(o => o.id === menuOfferId);
          if (offer) {
            setSelectedOffer(offer);
            setEditingOffer(offer);
            setOpenEditForm(true);
          }
          handleCloseMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edytuj
          </Box>
        </MenuItem>
        <MenuItem onClick={() => {
          const offer = offers.find(o => o.id === menuOfferId);
          if (offer) {
            setSelectedOffer(offer);
            setOpenDeleteDialog(true);
          }
          handleCloseMenu();
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            Usuń
          </Box>
        </MenuItem>
      </Menu>
      
      {/* Dialog szczegółów oferty */}
      {selectedOffer && (
        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#004273', 
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            Szczegóły oferty
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">ID Oferty</Typography>
                <Typography variant="body1">{selectedOffer.id}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Przygotowuje ofertę (PO)</Typography>
                <Typography variant="body1">{selectedOffer.offer_preparer || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Zamawiający</Typography>
                <Typography variant="body1">{selectedOffer.client_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Nazwa zamówienia</Typography>
                <Typography variant="body1">{selectedOffer.order_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Miejscowość</Typography>
                <Typography variant="body1">{selectedOffer.location || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Wizja lokalna</Typography>
                <Typography variant="body1">{selectedOffer.site_inspection}</Typography>
              </Box>
              {selectedOffer.site_inspection === 'Tak' && selectedOffer.site_inspection_date && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Termin wizji lokalnej (TWL)</Typography>
                  <Typography variant="body1">{formatDate(selectedOffer.site_inspection_date)}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Termin składania oferty (TSO)</Typography>
                <Typography variant="body1">{formatDate(selectedOffer.submission_deadline)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Szacunkowa wartość zamówienia (SWZ)</Typography>
                <Typography variant="body1">{formatPrice(selectedOffer.estimated_order_value)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Wadium</Typography>
                <Typography variant="body1">{formatPrice(selectedOffer.bid_bond)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Typography variant="body1">{renderStatus(selectedOffer.status)}</Typography>
              </Box>
              {selectedOffer.client_website && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Strona zamawiającego</Typography>
                  <Button
                    variant="text"
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(selectedOffer.client_website, '_blank')}
                    sx={{ textTransform: 'none', pl: 0 }}
                  >
                    Otwórz stronę
                  </Button>
                </Box>
              )}
              {selectedOffer.google_drive_link && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Google Drive</Typography>
                  <Button
                    variant="text"
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(selectedOffer.google_drive_link, '_blank')}
                    sx={{ textTransform: 'none', pl: 0 }}
                  >
                    Otwórz folder
                  </Button>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDetailsDialog(false)}>Zamknij</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Okno „Dostosuj kolumny” */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{ position: 'fixed', bottom: 0, right: 0, height: '50vh', width: '280px', display: 'flex', flexDirection: 'column', boxShadow: '0px 5px 30px rgba(0,0,0,0.15)', borderRadius: 0, zIndex: (theme) => theme.zIndex.modal + 2, backgroundColor: '#fff', pointerEvents: 'auto' }}
          onClick={(e)=> e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={()=>{ setColsAnchorEl(null); setDraftColumnsVisible(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(() => {
              const list = draftColumnsVisible ?? columnsVisible;
              if (draftColumnsVisible == null) setDraftColumnsVisible({ ...columnsVisible });
              const order: OfferColumnKey[] = ['oferta','preparer','client','twl','tso','swz','wadium','status','icons'];
              return order.map((key) => (
                <Box key={key} sx={{ py: 1, px: 2 }}>
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={()=>setDraftColumnsVisible((prev) => { const base: Record<OfferColumnKey, boolean> = { ...(prev ?? list ?? DEFAULT_VIS) } as Record<OfferColumnKey, boolean>; base[key] = !base[key]; return base; })} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>{key.toUpperCase()}</Typography>
                    </Box>
                  </Box>
                </Box>
              ));
            })()}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={()=>{ setColsAnchorEl(null); setDraftColumnsVisible(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button variant="contained" sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }} onClick={()=>{ const vis = draftColumnsVisible ?? columnsVisible; setColumnsVisible(vis); try { localStorage.setItem('offers.columns.visible', JSON.stringify(vis)); } catch {} setColsAnchorEl(null); setDraftColumnsVisible(null); setColResizeMode(false); }}>Zapisz zmiany</Button>
          </Box>
        </Paper>
      </Backdrop>
    </Box>
  );
};

export default OffersStandaloneTab;
